import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DataContext } from '@services/data-context';
import { MessageService } from '@services/messages';
import { ProjectContextService } from '@data-services/projects';
import { ProjectContext } from '@models/project-context';
import { customerMessagesUrlTree, isCustomerRole } from './customer-redirect';

export const projectAuthGuard: CanActivateFn = async (route, state) => {
  const dataContext = inject(DataContext);
  const messageService = inject(MessageService);
  const projectContextService = inject(ProjectContextService);
  const router = inject(Router);
  const translate = inject(TranslateService);

  const projectId = route.paramMap.get('projectId') || dataContext.projectContext()?.id;

  if (!projectId) {
    await rejectUserFromProject(router, messageService, translate);
    return false;
  }

  const existingContext = dataContext.projectContext();
  if (existingContext?.id === projectId && existingContext.currentUser) {
    if (isCustomerRole(existingContext.currentUser.userRole)) {
      return customerMessagesUrlTree(router, projectId);
    }

    refreshProjectContextInBackground(projectId, projectContextService, dataContext, router, messageService, translate);
    return true;
  }

  try {
    const projectContext: ProjectContext = await projectContextService.getProjectContextAsync(projectId);
    
    if (!projectContext.currentUser) {
      await rejectUserFromProject(router, messageService, translate);
      return false;
    }

    dataContext.setProjectContext(projectContext);

    if (isCustomerRole(projectContext.currentUser.userRole)) {
      return customerMessagesUrlTree(router, projectId);
    }

    return true;
  } catch (error) {
    console.error('Failed to load project context:', error);
    await rejectUserFromProject(router, messageService, translate);
    return false;
  }
};

async function refreshProjectContextInBackground(
  projectId: string,
  projectContextService: ProjectContextService,
  dataContext: DataContext,
  router: Router,
  messageService: MessageService,
  translate: TranslateService
): Promise<void> {
  try {
    const refreshedContext: ProjectContext = await projectContextService.getProjectContextAsync(projectId);
    dataContext.setProjectContext(refreshedContext);
    
    if (!refreshedContext.currentUser) {
      await rejectUserFromProject(router, messageService, translate);
    }
  } catch (error) {
    console.error('Failed to refresh project context:', error);
    await rejectUserFromProject(router, messageService, translate);
  }
}

async function rejectUserFromProject(
  router: Router,
  messageService: MessageService,
  translate: TranslateService
): Promise<void> {
  messageService.showErrorMessage(translate.instant('auth.errors.projectAccessDenied'));
  await router.navigate(['/projects']);
}

